import { CloseOutlined, MessageFilled, SendOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Form } from "components/Form/Form"
import { Input } from "components/Input/Input"
import { useNotification } from "components/Notification/Notification"
import { useDisclosure } from "hooks/useDisclosure"
import { Visit } from "interface/visit"
import { ComponentProps, KeyboardEventHandler, useCallback } from "react"
import { useAddComment } from "services/visits"
import { cn } from "styles/cn"
import { asDate, dateFormatter } from "utils/date"

export function Comments({
	visit,
	...props
}: { visit: Visit } & ComponentProps<"div">) {
	const { notify } = useNotification()
	const newComment = useDisclosure()
	const [form] = Form.useForm<FormValues>()
	const { mutateAsync: save, isPending } = useAddComment(visit.id)
	const handlePressEnter: KeyboardEventHandler<HTMLTextAreaElement> =
		useCallback(
			(e) => {
				if (e.key === "Enter") {
					if (e.shiftKey || e.altKey) {
						e.preventDefault()
						e.currentTarget.value += "\n"
					} else {
						e.preventDefault()
						form.submit()
					}
				}
			},
			[form],
		)
	const handleFinish = async ({ content }: FormValues) => {
		try {
			await save(content, {
				onSuccess: () => {
					newComment.close()
					form.resetFields()
					notify.success({
						message: "Add comment",
						description: "The new comment was added successfully",
					})
				},
			})
		} catch (error) {
			form.setFields([
				{
					name: "content",
					errors: [
						(error as Error).message ??
							"An unexpected error occurred while saving the comment.",
					],
				},
			])
		}
	}

	return (
		<section
			{...props}
			aria-label="Activity"
			className={cn(
				"flex flex-col gap-6",
				newComment.isOpen && "gap-3",
				props.className,
			)}
		>
			<div className="flex items-center justify-between gap-2">
				<h2 className="truncate font-nunito text-lg font-bold">Activity</h2>

				<Button
					icon={
						newComment.isOpen ? (
							<CloseOutlined aria-hidden />
						) : (
							<MessageFilled aria-hidden />
						)
					}
					onClick={() => {
						newComment.toggle()
						form.resetFields()
					}}
				>
					{newComment.isOpen ? "Close" : "Leave a comment"}
				</Button>
			</div>

			{newComment.isOpen && (
				<Form<FormValues>
					form={form}
					onFinish={handleFinish}
					className="relative"
				>
					<Form.Item<FormValues>
						name="content"
						rules={[
							{ required: true },
							{
								max: 500,
								message: "The comment cannot exceed 500 characters",
							},
						]}
					>
						<Input.TextArea
							autoSize={{ minRows: 5, maxRows: 12 }}
							readOnly={isPending}
							className="pr-12"
							placeholder="Write your comment..."
							onPressEnter={handlePressEnter}
						/>
					</Form.Item>
					<Form.Item noStyle>
						<Button
							htmlType="submit"
							type="primary"
							icon={<SendOutlined aria-hidden />}
							aria-label="Post comment"
							loading={isPending}
							className="absolute right-2 bottom-8"
						/>
					</Form.Item>
				</Form>
			)}

			<ul
				className="flex flex-col gap-4"
				aria-label={`${visit.comments?.length ?? 0} comments available`}
			>
				{visit.comments
					?.toSorted(
						(a, b) => asDate(b.createdAt).unix() - asDate(a.createdAt).unix(),
					)
					.map(({ id, content, author, createdAt }) => {
						const creationDate = dateFormatter(createdAt)

						return (
							<li
								key={id}
								className="flex flex-col gap-2 rounded-xl border border-mint-20 bg-white p-4 whitespace-pre-line shadow-2xs"
								aria-label={`Commented by ${author} on ${creationDate}`}
							>
								{content}
								<div className="flex items-center justify-between gap-4 text-navy-60">
									{author}
									<time dateTime={createdAt}>{creationDate}</time>
								</div>
							</li>
						)
					})}
			</ul>
		</section>
	)
}

interface FormValues {
	content: string
}
